/**
 * Where a quiz / exam appears in the Question bank tree — the same levels as the tree itself:
 *   Subject (top-level category) → Content (a sub-category) → Course → Chapter / Topic / Subtopic.
 *
 *   <BankLocationPicker api value={{ subject_id, category_id, course_id, node_id }} onChange={setValue} />
 *
 * Everything below Subject is optional: the quiz lands at the deepest level chosen. The exam record
 * stores bank_category_id (= content or subject), bank_course_id and bank_node_id.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Label } from '../ui/label';
import { useCategories } from '../categories/CategoryManager';

export const EMPTY_BANK_LOCATION = { subject_id: '', category_id: '', course_id: '', node_id: '' };
export const bankLocationToExam = (loc) => ({ bank_category_id: loc.category_id || loc.subject_id || '', bank_course_id: loc.course_id || '', bank_node_id: loc.node_id || '' });

const NODE_LABEL = { section: 'Chapter', subsection: 'Topic' };
const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs font-medium">{label}</Label>{children}</div>;
const pad = (d) => '   '.repeat(Math.max(0, d));

export default function BankLocationPicker({ api, value, onChange, compact = false, categoryOnly = false }) {
  const loc = { ...EMPTY_BANK_LOCATION, ...(value || {}) };
  const { categories } = useCategories(api);
  const [courses, setCourses] = useState([]);
  const [nodes, setNodes] = useState([]);

  const subjects = categories.filter((c) => !c.depth);
  const subject = categories.find((c) => c.id === loc.subject_id);
  const contents = useMemo(() => (subject ? categories.filter((c) => c.id !== subject.id && (c.path || '').startsWith(`${subject.path} / `)) : []), [categories, subject]);

  // courses in the chosen category (incl. sub-categories)
  useEffect(() => {
    const cat = loc.category_id || loc.subject_id;
    if (!cat) { setCourses([]); return undefined; }
    let live = true;
    api.get(`/courses?category_id=${cat}`).then((r) => { if (live) setCourses(r.data || []); }).catch(() => { if (live) setCourses([]); });
    return () => { live = false; };
  }, [api, loc.category_id, loc.subject_id]);

  // containers (chapters / topics / subtopics with children) of the chosen course
  useEffect(() => {
    if (!loc.course_id) { setNodes([]); return undefined; }
    let live = true;
    api.get(`/courses/${loc.course_id}/nodes?flat=true`).then((r) => {
      if (!live) return;
      const all = r.data || [];
      const hasKids = new Set(all.map((n) => n.parent_id).filter(Boolean));
      const keep = all.filter((n) => n.node_type === 'section' || n.node_type === 'subsection' || hasKids.has(n.id));
      const byParent = {}; keep.forEach((n) => { (byParent[n.parent_id || '__root'] = byParent[n.parent_id || '__root'] || []).push(n); });
      const out = []; const walk = (pid, depth) => (byParent[pid || '__root'] || []).sort((a, b) => (a.order_index || 0) - (b.order_index || 0)).forEach((n) => { out.push({ ...n, depth }); walk(n.id, depth + 1); });
      walk(null, 0);
      setNodes(out);
    }).catch(() => { if (live) setNodes([]); });
    return () => { live = false; };
  }, [api, loc.course_id]);

  const set = (patch) => onChange({ ...loc, ...patch });

  return (
    <div className={compact ? 'grid sm:grid-cols-2 gap-3' : 'space-y-3'} data-testid="bank-location">
      <Field label="Category — subject">
        <select className="form-select w-full" value={loc.subject_id} onChange={(e) => set({ subject_id: e.target.value, category_id: '', course_id: '', node_id: '' })} data-testid="bank-subject">
          <option value="">Choose a subject…</option>
          {subjects.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Under which content">
        <select className="form-select w-full" value={loc.category_id} onChange={(e) => set({ category_id: e.target.value, course_id: '', node_id: '' })} disabled={!loc.subject_id} data-testid="bank-content">
          <option value="">{loc.subject_id ? '— directly under the subject —' : 'Choose a subject first'}</option>
          {contents.map((c) => <option key={c.id} value={c.id}>{pad((c.depth || 1) - 1)}{(c.depth || 1) > 1 ? '└ ' : ''}{c.name}</option>)}
        </select>
      </Field>
      {categoryOnly ? null : (
      <Field label="Course">
        <select className="form-select w-full" value={loc.course_id} onChange={(e) => set({ course_id: e.target.value, node_id: '' })} disabled={!loc.subject_id} data-testid="bank-course">
          <option value="">{loc.subject_id ? (courses.length ? '— at the category level —' : 'No courses in this category') : 'Choose a subject first'}</option>
          {courses.map((c) => <option key={c.id} value={c.id}>{c.title}{c.is_published ? '' : ' (draft)'}</option>)}
        </select>
      </Field>
      )}
      {categoryOnly ? null : (
      <Field label="Chapter / topic">
        <select className="form-select w-full" value={loc.node_id} onChange={(e) => set({ node_id: e.target.value })} disabled={!loc.course_id} data-testid="bank-node">
          <option value="">{loc.course_id ? '— at the course level —' : 'Choose a course first'}</option>
          {nodes.map((n) => <option key={n.id} value={n.id}>{pad(n.depth)}{n.depth ? '└ ' : ''}{n.title} ({NODE_LABEL[n.node_type] || 'Subtopic'})</option>)}
        </select>
      </Field>
      )}
    </div>
  );
}

/** Work out subject / content from an exam's stored bank_category_id (needs the category list). */
export function bankLocationFromExam(exam, categories) {
  const loc = { ...EMPTY_BANK_LOCATION, course_id: exam?.bank_course_id || '', node_id: exam?.bank_node_id || '' };
  if (!exam?.bank_category_id) return loc;
  let c = categories.find((x) => x.id === exam.bank_category_id);
  const leaf = c;
  while (c && c.parent_id) c = categories.find((x) => x.id === c.parent_id) || null;
  loc.subject_id = c?.id || '';
  loc.category_id = leaf && c && leaf.id !== c.id ? leaf.id : '';
  return loc;
}
